import type { StockForgeApiConfig } from "../types";
import { proxyFetch } from "./proxyFetch";

/**
 * Stock Forge API client.
 * In production, calls go through your Linode proxy (API key stays server-side).
 * Docs: https://thestockforge.com/docs
 */
export class StockForgeApi {
  constructor(private config: StockForgeApiConfig) {}

  private headers(): HeadersInit {
    return {
      Accept: "application/json",
    };
  }

  private async request<T = unknown>(
    path: string,
    init: RequestInit = {},
    opts: { bypassCache?: boolean } = {},
  ): Promise<T> {
    const url = `${this.config.baseUrl.replace(/\/$/, "")}${path}`;
    const headers: Record<string, string> = {
      ...this.headers(),
      ...(init.headers as Record<string, string> | undefined),
    };
    // Tell the Linode proxy to skip its GET cache and refresh from Stock Forge.
    if (opts.bypassCache) {
      headers["Cache-Control"] = "no-cache";
      headers["X-SFE-Refresh"] = "1";
    }
    const res = await proxyFetch(url, {
      ...init,
      headers,
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`${path}: ${res.status} ${res.statusText}${body ? ` — ${body.slice(0, 120)}` : ""}`);
    }
    return res.json() as Promise<T>;
  }

  async getPutRecommendations(): Promise<unknown> {
    return this.request("/api/v1/wheel/recommendations/puts");
  }

  async getCallRecommendations(): Promise<unknown> {
    return this.request("/api/v1/wheel/recommendations/calls");
  }

  async getWheelMeta(opts: { bypassCache?: boolean } = {}): Promise<unknown> {
    return this.request("/api/v1/wheel/meta", {}, opts);
  }

  async getPositions(): Promise<unknown> {
    return this.request("/api/v1/wheel/positions");
  }

  async getStockPositions(): Promise<unknown> {
    return this.request("/api/v1/wheel/stock-positions");
  }

  async getSettings(): Promise<unknown> {
    return this.request("/api/v1/wheel/settings");
  }
}
